import { Link } from 'react-router-dom';
import './styles.css';

export default function SipWealthHome() {
  return (
    <div className="sip-home">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="gradient-text">SIP Wealth Calculator</span>
            </h1>
            <p className="hero-subtitle">
              Plan your financial future with precision and confidence
            </p>
            <p className="hero-description">
              A comprehensive Flutter application for calculating Systematic Investment Plan (SIP) 
              returns with advanced features like step-up SIP, inflation adjustments, and detailed analytics.
            </p>
            <div className="hero-buttons">
              <a 
                href="https://apps.apple.com/app/id6753896869" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                <svg className="btn-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Download on App Store
              </a>
              <a 
                href="https://play.google.com/store/apps/details?id=com.neerajkumar.sipwealthcalculator" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                <svg className="btn-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                Get it on Google Play
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <h2 className="section-title">Powerful Features</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>SIP Calculator</h3>
              <p>Calculate maturity amounts for systematic investments with precision</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📈</div>
              <h3>Step-Up SIP</h3>
              <p>Support for increasing monthly investments annually to boost returns</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💹</div>
              <h3>Inflation Adjustment</h3>
              <p>Real-time inflation impact calculations for accurate planning</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🌍</div>
              <h3>Multi-Currency</h3>
              <p>Support for INR, USD, EUR, GBP with proper formatting</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📉</div>
              <h3>Interactive Charts</h3>
              <p>Beautiful visualizations to track your investment growth</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📄</div>
              <h3>PDF Export</h3>
              <p>Generate professional reports with charts and detailed tables</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔖</div>
              <h3>Bookmarks</h3>
              <p>Save and manage multiple calculation scenarios</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎨</div>
              <h3>Dark/Light Theme</h3>
              <p>System-aware theming for comfortable viewing anytime</p>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Features Section */}
      <section className="premium">
        <div className="container">
          <div className="premium-content">
            <div className="premium-badge">⭐ Premium</div>
            <h2 className="section-title">Premium Features</h2>
            <p className="premium-subtitle">Everything you need for SIP wealth planning</p>
            <div className="premium-features">
              <div className="premium-item">
                <span className="checkmark">✓</span>
                <span>Step-Up SIP Calculations</span>
              </div>
              <div className="premium-item">
                <span className="checkmark">✓</span>
                <span>Inflation Adjustments</span>
              </div>
              <div className="premium-item">
                <span className="checkmark">✓</span>
                <span>Beautiful Charts & Graphs</span>
              </div>
              <div className="premium-item">
                <span className="checkmark">✓</span>
                <span>Detailed Year-wise Tables</span>
              </div>
              <div className="premium-item">
                <span className="checkmark">✓</span>
                <span>Professional PDF Reports</span>
              </div>
              <div className="premium-item">
                <span className="checkmark">✓</span>
                <span>Unlimited Bookmarks</span>
              </div>
              <div className="premium-item">
                <span className="checkmark">✓</span>
                <span>Export & Share Results</span>
              </div>
              <div className="premium-item">
                <span className="checkmark">✓</span>
                <span>Lifetime Updates</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works">
        <div className="container">
          <h2 className="section-title">How It Works</h2>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Enter Your Details</h3>
              <p>Input your monthly investment amount, investment period, and expected returns</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Customize Parameters</h3>
              <p>Enable step-up SIP, adjust for inflation, and select your preferred currency</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>View Results</h3>
              <p>Get detailed breakdowns with interactive charts and year-wise projections</p>
            </div>
            <div className="step">
              <div className="step-number">4</div>
              <h3>Export & Share</h3>
              <p>Generate PDF reports, bookmark scenarios, and share with your financial advisor</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Highlights */}
      <section className="technical">
        <div className="container">
          <h2 className="section-title">Built with Excellence</h2>
          <div className="tech-grid">
            <div className="tech-item">
              <div className="tech-icon">🚀</div>
              <h4>Flutter Framework</h4>
              <p>Cross-platform native performance</p>
            </div>
            <div className="tech-item">
              <div className="tech-icon">💾</div>
              <h4>Offline Support</h4>
              <p>Works without internet connection</p>
            </div>
            <div className="tech-item">
              <div className="tech-icon">🔒</div>
              <h4>Data Privacy</h4>
              <p>All data stored locally on your device</p>
            </div>
            <div className="tech-item">
              <div className="tech-icon">⚡</div>
              <h4>Fast & Responsive</h4>
              <p>Optimized for all screen sizes</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <h2 className="cta-title">Start Planning Your Financial Future Today</h2>
          <p className="cta-description">
            Download SIP Wealth Calculator and take control of your investments
          </p>
          <div className="cta-buttons">
            <a 
              href="https://apps.apple.com/app/id6753896869" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-large btn-primary"
            >
              <svg className="btn-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              Download on App Store
            </a>
            <a 
              href="https://play.google.com/store/apps/details?id=com.neerajkumar.sipwealthcalculator" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-large btn-secondary"
            >
              <svg className="btn-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              Get it on Google Play
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>SIP Wealth Calculator</h3>
              <p>Plan your financial future with precision and confidence</p>
            </div>
            <div className="footer-section">
              <h4>Quick Links</h4>
              <nav className="footer-nav">
                <Link to="/sip-wealth-calculator/support">Support</Link>
                <Link to="/sip-wealth-calculator/privacy-policy">Privacy Policy</Link>
                <Link to="/sip-wealth-calculator/terms-and-conditions">Terms & Conditions</Link>
              </nav>
            </div>
            <div className="footer-section">
              <h4>Contact</h4>
              <p>
                <a href="mailto:sainineeraj1234@gmail.com">sainineeraj1234@gmail.com</a>
              </p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 SIP Wealth Calculator. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
